/**
 * Advisor Passport Page
 * 
 * Displays advisor intelligence metrics, decision quality, workflow impact,
 * and learning recommendations. Catalyst+ feature.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { AdvisorSnapshotCard } from '@/components/advisor-passport/AdvisorSnapshotCard';
import { AdvisorDecisionQuality } from '@/components/advisor-passport/AdvisorDecisionQuality';
import { AdvisorDiagnosticTranslation } from '@/components/advisor-passport/AdvisorDiagnosticTranslation';
import { AdvisorWorkflowImpact } from '@/components/advisor-passport/AdvisorWorkflowImpact';
import { AdvisorLearningRecommendations } from '@/components/advisor-passport/AdvisorLearningRecommendations';
import { AdvisorActivityCard } from '@/components/advisor-passport/AdvisorActivityCard';
import { PassportCertificationsSection } from '@/components/passport/PassportCertificationsSection';

import { useAdvisorIntelligenceStats, useCanViewAdvisorStats } from '@/hooks/useAdvisorIntelligenceStats';
import { RequireFeature } from '@/components/access/RequireFeature';
import { useAuth } from '@/contexts/AuthContext';

export default function AdvisorPassport() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  // FIX-R133-HIGH-2: the legacy expression on the certifications card
  // (line ~167) was `userId === activeShop?.id ? false : true`. It
  // compared a user UUID to a shop UUID — disjoint namespaces, always
  // unequal — so isOwner was permanently `true`. That handed
  // owner-mode UI (Add / Edit / Delete + Visibility toggle) to anyone
  // viewing another advisor's passport. Database RLS (WITH CHECK
  // auth.uid() = user_id on passport_certifications) still blocked the
  // actual writes, but the UX was misleading and a clear Passport
  // ownership-boundary visualization bug.
  const { user, activeShop } = useAuth();
  const isSelfView = !!user?.id && userId === user.id;

  // Fetch advisor profile info
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['advisor-profile', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, email')
        .eq('id', userId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Check visibility permissions (Tweak #1)
  const { data: accessInfo, isLoading: accessLoading } = useCanViewAdvisorStats(userId);

  // Fetch intelligence stats
  const { data: stats, isLoading: statsLoading } = useAdvisorIntelligenceStats(userId);

  const isLoading = profileLoading || accessLoading || statsLoading;

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading advisor profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Advisor profile not found</p>
          <Button onClick={handleGoBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Access denied view (Tweak #1: Visibility rules)
  if (!accessInfo?.canView) {
    return (
      <div className="container mx-auto p-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleGoBack}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle>Access Restricted</CardTitle>
            <CardDescription>
              {accessInfo?.isSelfView 
                ? accessInfo?.reason === 'self_view_not_enabled'
                  ? 'Self-view has not been enabled by your manager yet.'
                  : 'Self-view is pending. Please check back after the unlock period.'
                : 'You do not have permission to view this advisor\'s intelligence profile.'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <RequireFeature feature="advisorPassports" fallback={
      <div className="container mx-auto p-6 text-center">
        <p className="text-muted-foreground">Advisor Passports require Catalyst tier or higher.</p>
      </div>
    }>
      <div className="container mx-auto p-4 md:p-6 space-y-6 max-w-6xl pb-20 overflow-y-auto">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleGoBack}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Section 1: Snapshot Card */}
        <AdvisorSnapshotCard 
          profile={profile}
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 2: Decision Quality */}
        <AdvisorDecisionQuality 
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 3: Diagnostic Translation (Catalyst+) */}
        <AdvisorDiagnosticTranslation 
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 4: Workflow Impact */}
        <AdvisorWorkflowImpact 
          stats={stats}
          isSelfView={accessInfo?.isSelfView || false}
        />

        {/* Section 5: Learning Recommendations */}
        <AdvisorLearningRecommendations
          userId={userId!}
        />

        {/* Slice 4E: Advisor Activity foundation. Flag-gated by */}
        {/* `advisorTimingAnalytics`; renders nothing when off. */}
        {/* Activity-based: counts/timing apply to anyone (OWNER, */}
        {/* MANAGER, SERVICE_WRITER, ORG_ADMIN) who performs FOH work. */}
        <AdvisorActivityCard advisorUserId={userId!} />

        {/* Certifications */}
        <PassportCertificationsSection
          userId={userId!}
          isOwner={isSelfView}
        />
      </div>
    </RequireFeature>
  );
}
